import { SearchOption } from "@/models/base/SearchOption";
import RequestMethod from "../models/client/RequestMethod";
import RestResponse from "../models/client/RestResponse";
import Conversation from "../models/conversation/Conversation";
import {
  CrudServiceType,
  crudService,
  handlePaginationQuery,
} from "./crudService";
import { RestClient } from "./restClient";
import { Pagination, PaginationQuery } from "@/models/base/Pagination";
import { SendMessageContextFilter } from "./messageService";
import type { ConversationWorkspaceDocumentSnapshot } from "@/lib/buildConversationWorkspaceDocuments";

export type ConversationWithPageNumber = Conversation & {
  pageNumber: number;
};

export type ConversationServiceType = CrudServiceType<Conversation> & {
  createConversation: (
    searchOptions: SearchOption[],
    workspaceFilter: SendMessageContextFilter[],
    toneOfVoiceId?: string,
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[]
  ) => RestResponse<Conversation>;
  getConversationById: (
    id: string,
    pageSize: number
  ) => RestResponse<ConversationWithPageNumber>;
  getAllConversations: (
    query: PaginationQuery
  ) => RestResponse<Pagination<Conversation>>;
  deleteAsAdmin: (id: string) => RestResponse<void>;
};

const conversationServices = (
  client: RestClient,
  path: string
): ConversationServiceType => ({
  ...crudService<Conversation>(client, path),
  createConversation: async (
    searchOptions: SearchOption[],
    workspaceFilter: SendMessageContextFilter[],
    toneOfVoiceId?: string,
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[]
  ) =>
    await client.fetch<Conversation>(RequestMethod.POST, `${path}`, {
      searchOptions,
      workspaceFilter,
      toneOfVoiceId,
      workspaceDocuments,
    }),
  getConversationById: async (id: string, pageSize: number) =>
    await client.fetch<ConversationWithPageNumber>(
      RequestMethod.GET,
      `${path}/${id}/page?pageSize=${pageSize}`
    ),
  getAllConversations: async (query: PaginationQuery) => {
    const response = await client.fetch<Pagination<Conversation>>(
      RequestMethod.GET,
      `${path}/all?${handlePaginationQuery(query)}`,
      undefined,
      undefined,
      undefined,
      undefined
    );
    return response;
  },
  deleteAsAdmin: async (id: string) =>
    await client.fetch<void>(RequestMethod.DELETE, `${path}/${id}/admin`),
});

export default conversationServices;
